// What to order (purchasing phase 2, docs/plans/reorder.md): the products at or under their
// alert level once open orders count, by usual supplier, with an amount to order that can be
// changed, and one draft purchase order per supplier made from what is ticked. A product
// can go to another supplier for this order, alone or with others of its supplier, or have
// that supplier saved as its usual one (docs/plans/reorder-move.md). A tab of Purchase
// orders; every plan.
import { purchaseLineCost } from '@ims/core'
import type { RowOf, ToOrderRow } from '@ims/data'
import {
  Badge,
  Button,
  ConfirmDialog,
  cx,
  EmptyState,
  IconTile,
  Kbd,
  ListToolbar,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  Modal,
  PageHeader,
  Select,
  SkeletonTable,
  useToast,
} from '@ims/ui'
import {
  ChevronDown,
  ClipboardList,
  Ellipsis,
  Info,
  Minus,
  PackageCheck,
  Plus,
  Search,
  Truck,
} from '@ims/ui/icons'
import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { QtyInput, useLookups } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { money, qty, todayIso } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import {
  buildReorderDrafts,
  groupBySupplier,
  suggestQty,
  type DraftPlan,
  type ReorderGroup,
} from '../lib/reorder.ts'
import { updateOp } from '../lib/rows.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { SectionTabs } from '../layout/sections.tsx'

/** What the list reads: a change to any of them can move a product on or off it. */
export const TO_ORDER_TABLES = [
  'products',
  'stock_movements',
  'purchase_orders',
  'purchase_order_lines',
  'goods_receipt_lines',
  'suppliers',
] as const

// Checkbox, product, in stock, on order, from, order, cost, amount. Every group uses the
// same columns, so they line up from one card to the next.
const GRID =
  'sm:grid sm:grid-cols-[24px_minmax(0,1.7fr)_minmax(0,0.7fr)_minmax(0,0.6fr)_48px_150px_minmax(0,0.6fr)_minmax(0,0.7fr)] sm:items-center sm:gap-3'
const BOX = 'size-4 shrink-0 rounded border-border accent-primary'

type SupplierRow = RowOf<'suppliers'>

/** The moves without these products: they are back under their usual supplier. */
const without = (moved: Readonly<Record<string, string>>, rows: readonly ToOrderRow[]) => {
  const next = { ...moved }
  for (const r of rows) delete next[r.product.id]
  return next
}

const totalOf = (plans: readonly DraftPlan[]) =>
  plans.reduce((sum, p) => sum + p.lines.reduce((s, l) => s + purchaseLineCost(l), 0), 0)

export function WhatToOrder() {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const canEdit = useCan('staff')
  const toast = useToast()
  const navigate = useNavigate()
  const [locationId, setLocationId] = useState('')
  // Amounts changed by hand, and the products unticked, by product id.
  const [amounts, setAmounts] = useState<Record<string, number | null>>({})
  const [off, setOff] = useState<ReadonlySet<string>>(new Set())
  const [active, setActive] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<DraftPlan[] | null>(null)
  const [busy, setBusy] = useState(false)
  // Products put under another supplier for this order only: product id → supplier id.
  const [moved, setMoved] = useState<Readonly<Record<string, string>>>({})
  // The Move products dialog, with the supplier it starts from ('' for none chosen).
  const [moving, setMoving] = useState<{ from: string } | null>(null)

  const rows = useLive(
    (r) => r.purchases.toOrder?.({ locationId: locationId || undefined }) ?? Promise.resolve([]),
    [locationId],
    [...TO_ORDER_TABLES],
  ).data
  const suppliers =
    useLive(
      (r) => r.list('suppliers', { orderBy: { column: 'name' }, limit: 2000 }),
      [],
      ['suppliers'],
    ).data?.rows ?? []
  const groups = rows ? groupBySupplier(rows, suppliers, moved) : []
  const nameOf = (id: string | null | undefined) =>
    suppliers.find((s) => s.id === id)?.name ?? 'the supplier'

  const unitOf = (row: ToOrderRow) => lookups.unit(row.product.unit_id)
  const amountOf = (row: ToOrderRow) => {
    const set = amounts[row.product.id]
    return set !== undefined ? set : suggestQty(row, unitOf(row)?.allows_decimal ?? false)
  }
  const costOf = (row: ToOrderRow, milli: number | null) =>
    row.product.cost_price_paisa === null || !milli
      ? 0
      : purchaseLineCost({ qtyMilli: milli, unitCostPaisa: row.product.cost_price_paisa })
  const ticked = (group: ReorderGroup, row: ToOrderRow) =>
    group.supplierId !== null && !off.has(row.product.id) && (amountOf(row) ?? 0) > 0
  const planOf = (group: ReorderGroup): DraftPlan | null => {
    if (!group.supplierId) return null
    const lines = group.rows
      .filter((r) => ticked(group, r))
      .map((r) => ({
        productId: r.product.id,
        qtyMilli: amountOf(r)!,
        unitCostPaisa: r.product.cost_price_paisa ?? 0,
      }))
    return lines.length > 0 ? { supplierId: group.supplierId, lines } : null
  }
  const plans = groups.map(planOf).filter((p): p is DraftPlan => p !== null)
  const where = locationId || lookups.activeLocations[0]?.id || null
  const whereName = lookups.activeLocations.find((l) => l.id === where)?.name

  const tick = (id: string, on: boolean) =>
    setOff((s) => {
      const next = new Set(s)
      if (on) next.delete(id)
      else next.add(id)
      return next
    })
  const step = (row: ToOrderRow, by: 1 | -1) =>
    setAmounts((a) => ({
      ...a,
      [row.product.id]: Math.max(0, (amountOf(row) ?? 0) + by * 1000),
    }))
  /**
   * Puts products under another supplier. With `usual` it is saved on each product (one
   * write); otherwise only this order changes, and Undo puts them back.
   */
  const move = async (chosen: readonly ToOrderRow[], supplierId: string, usual: boolean) => {
    if (chosen.length === 0) return
    const one = chosen.length === 1 ? chosen[0]!.product : null
    const what = one ? one.name : `${chosen.length} products`
    if (usual) {
      try {
        await repo.commit(
          chosen.map((r) => updateOp('products', r.product, { supplier_id: supplierId })),
        )
        setMoved((m) => without(m, chosen))
        toast.success(
          `${what} ${one ? 'is' : 'are'} ordered from ${nameOf(supplierId)} from now on`,
        )
      } catch (error) {
        toast.error(errorMessage(error))
      }
      return
    }
    const before = moved
    setMoved((m) => {
      const next = { ...without(m, chosen) }
      for (const r of chosen)
        if (r.product.supplier_id !== supplierId) next[r.product.id] = supplierId
      return next
    })
    const usualOf = one?.supplier_id && one.supplier_id !== supplierId ? one.supplier_id : null
    toast.success(`${what} ${one ? 'goes' : 'go'} on the ${nameOf(supplierId)} order`, {
      detail: usualOf
        ? `${nameOf(usualOf)} stays its usual supplier.`
        : one
          ? undefined
          : 'Their usual suppliers stay as they are.',
      action: { label: 'Undo', onClick: () => setMoved(before) },
    })
  }

  const make = async (chosen: DraftPlan[]) => {
    if (!current) return
    setBusy(true)
    try {
      const { orderIds, ops } = buildReorderDrafts(
        { tenantId: current.tenant_id, now: new Date().toISOString() },
        chosen,
        { locationId: where, orderDate: todayIso() },
      )
      await repo.commit(ops)
      setConfirm(null)
      toast.success(
        orderIds.length === 1 ? 'Draft order made' : `${orderIds.length} draft orders made`,
      )
      navigate(
        orderIds.length === 1 ? `/purchase-orders/${orderIds[0]}` : '/purchase-orders?status=draft',
      )
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const all = groups.flatMap((g) => g.rows.map((r) => ({ group: g, row: r })))
  const activeRow = all.find((x) => x.row.product.id === active)
  const listRef = useRef<HTMLDivElement>(null)
  useListKeys({
    ids: all.map((x) => x.row.product.id),
    active,
    setActive,
    list: listRef,
    open: () => active && navigate(`/products/${active}`),
    keys: {
      ' ': () => activeRow?.group.supplierId && tick(active!, off.has(active!)),
      '+': () => activeRow && step(activeRow.row, 1),
      '=': () => activeRow && step(activeRow.row, 1),
      '-': () => activeRow && step(activeRow.row, -1),
      m: () =>
        activeRow &&
        listRef.current
          ?.querySelector<HTMLButtonElement>(
            `[aria-label="${CSS.escape(`Supplier for ${activeRow.row.product.name}`)}"]`,
          )
          ?.click(),
    },
  })

  const out = rows?.filter((r) => r.stockMilli <= 0).length ?? 0
  const withSupplier = groups.filter((g) => g.supplierId).length
  const totalAll = all.reduce((sum, { row }) => sum + costOf(row, amountOf(row)), 0)

  return (
    <div>
      <PageHeader
        title="Purchase orders"
        subtitle={
          rows && rows.length > 0 ? (
            <>
              <b className="font-semibold text-ink">
                {rows.length} product{rows.length === 1 ? '' : 's'}
              </b>{' '}
              at or below their alert level
              {out > 0 && <span className="font-semibold text-danger"> · {out} out of stock</span>}
              {withSupplier > 0 && ` · ${withSupplier} supplier${withSupplier === 1 ? '' : 's'}`}
              {totalAll > 0 && (
                <>
                  {' · '}
                  <b className="font-semibold text-ink">{money(totalAll, settings)}</b> if you order
                  it all
                </>
              )}
            </>
          ) : (
            'Products at or below their alert level, once what is on order arrives.'
          )
        }
        secondary={
          canEdit &&
          groups.length > 0 &&
          suppliers.some((s) => s.is_active) && (
            <Button variant="secondary" onClick={() => setMoving({ from: '' })}>
              <Truck className="size-4" aria-hidden />
              Move products…
            </Button>
          )
        }
        actions={
          canEdit &&
          plans.length > 0 && (
            <Button className="max-sm:hidden" onClick={() => setConfirm(plans)}>
              <ClipboardList className="size-4" aria-hidden />
              Make {plans.length} draft order{plans.length === 1 ? '' : 's'}
            </Button>
          )
        }
      />
      <SectionTabs section="purchase" />
      {/* With one location there is nothing to choose: no empty toolbar under the tabs. */}
      {lookups.activeLocations.length > 1 && (
        <ListToolbar>
          <label className="flex items-center gap-2 text-sm text-muted max-sm:w-full">
            For
            <Select
              dense
              aria-label="For which location"
              value={locationId}
              onChange={(e) => {
                setLocationId(e.target.value)
                setActive(null)
              }}
              className="max-sm:flex-1"
            >
              <option value="">All locations</option>
              {lookups.activeLocations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </label>
        </ListToolbar>
      )}
      {!rows ? (
        <SkeletonTable cols={5} />
      ) : rows.length === 0 ? (
        <div className="rounded-card bg-surface shadow-card">
          <EmptyState
            icon={<PackageCheck className="size-6" />}
            title="Nothing to order"
            description="No product is at or below its alert level once what is on order arrives. Give products an alert level or a reorder quantity to see them here."
          />
        </div>
      ) : (
        <div ref={listRef} className="flex flex-col gap-3">
          <p className="flex items-start gap-2.5 rounded-xl bg-sell-wash px-3.5 py-2.5 text-[13.5px] text-sell">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              Suggested: the product’s <b>reorder quantity</b>, or enough to reach twice its alert
              level. What is already on order is taken off. Change any number first.
            </span>
          </p>
          {groups.map((group, i) => (
            <GroupCard
              key={group.supplierId ?? 'none'}
              group={group}
              columns={i === 0}
              canEdit={canEdit}
              suppliers={suppliers}
              active={active}
              settings={settings}
              unitOf={unitOf}
              amountOf={amountOf}
              costOf={costOf}
              ticked={(r) => ticked(group, r)}
              onTick={tick}
              onAmount={(id, milli) => setAmounts((a) => ({ ...a, [id]: milli }))}
              onStep={step}
              moved={moved}
              nameOf={nameOf}
              onMove={(r, id, usual) => void move(r, id, usual)}
              onMoveAll={() => setMoving({ from: group.supplierId ?? NONE })}
              onBack={(r) => setMoved((m) => without(m, [r]))}
              onMake={() => {
                const plan = planOf(group)
                if (plan) setConfirm([plan])
              }}
              planned={planOf(group) !== null}
            />
          ))}
          {canEdit && plans.length > 0 && (
            // On a phone the main button waits at the bottom, over the list.
            <div className="sticky bottom-24 z-10 flex items-center gap-2.5 rounded-card bg-ink px-3 py-2.5 text-surface shadow-card sm:hidden">
              <span className="flex-1 text-[13px]">
                {plans.reduce((n, p) => n + p.lines.length, 0)} ticked
                <b className="block text-[15px] tabular">{money(totalOf(plans), settings)}</b>
              </span>
              <Button onClick={() => setConfirm(plans)}>
                <ClipboardList className="size-4" aria-hidden />
                Make {plans.length === 1 ? 'the draft' : `${plans.length} drafts`}
              </Button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 text-xs text-muted">
            <span>
              Grouped by each product’s usual supplier; From moves one for this order. Drafts are
              made, nothing is sent.
            </span>
            <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
              <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Space</Kbd> tick <Kbd>+</Kbd> <Kbd>−</Kbd> amount{' '}
              <Kbd>M</Kbd> supplier <Kbd>Enter</Kbd> open
            </span>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirm !== null}
        title={`Make ${confirm?.length ?? 0} draft order${confirm?.length === 1 ? '' : 's'}?`}
        confirmLabel={
          confirm?.length === 1 ? 'Make the draft' : `Make ${confirm?.length ?? 0} drafts`
        }
        busy={busy}
        onConfirm={() => confirm && void make(confirm)}
        onClose={() => setConfirm(null)}
      >
        <div className="grid gap-2">
          {confirm?.map((plan) => {
            const total = totalOf([plan])
            return (
              <div
                key={plan.supplierId}
                className="flex items-center gap-2.5 rounded-xl border border-border px-3 py-2.5"
              >
                <IconTile area="buy" size="sm">
                  <Truck className="size-4" />
                </IconTile>
                <span className="min-w-0 flex-1">
                  <b className="block truncate">
                    {suppliers.find((s) => s.id === plan.supplierId)?.name ?? 'Supplier'}
                  </b>
                  <span className="text-[12.5px] text-muted">
                    {plan.lines.length} line{plan.lines.length === 1 ? '' : 's'}
                    {whereName && ` · for ${whereName}`}
                  </span>
                </span>
                <b className="tabular">{money(total, settings)}</b>
              </div>
            )
          })}
        </div>
        <p className="mt-3 text-[13px] text-muted">
          They open as drafts: check each and send it. Where approval is on, sending submits it for
          approval.
        </p>
      </ConfirmDialog>
      {moving && (
        <MoveDialog
          key={moving.from}
          from={moving.from}
          groups={groups}
          suppliers={suppliers}
          busy={busy}
          onClose={() => setMoving(null)}
          onMove={(chosen, to, usual) => {
            setMoving(null)
            void move(chosen, to, usual)
          }}
        />
      )}
    </div>
  )
}

/** The From choice for products with no usual supplier. */
const NONE = 'none'

function GroupCard({
  group,
  columns,
  canEdit,
  suppliers,
  active,
  settings,
  unitOf,
  amountOf,
  costOf,
  ticked,
  onTick,
  onAmount,
  onStep,
  moved,
  nameOf,
  onMove,
  onMoveAll,
  onBack,
  onMake,
  planned,
}: {
  group: ReorderGroup
  /** The column names, over the first group only. */
  columns: boolean
  canEdit: boolean
  suppliers: SupplierRow[]
  active: string | null
  settings: ReturnType<typeof useSettings>
  unitOf: (row: ToOrderRow) => RowOf<'units'> | null
  amountOf: (row: ToOrderRow) => number | null
  costOf: (row: ToOrderRow, milli: number | null) => number
  ticked: (row: ToOrderRow) => boolean
  onTick: (id: string, on: boolean) => void
  onAmount: (id: string, milli: number | null) => void
  onStep: (row: ToOrderRow, by: 1 | -1) => void
  moved: Readonly<Record<string, string>>
  nameOf: (id: string | null | undefined) => string
  onMove: (rows: ToOrderRow[], supplierId: string, usual: boolean) => void
  onMoveAll: () => void
  /** Back under its usual supplier (the Undo on a moved row). */
  onBack: (row: ToOrderRow) => void
  onMake: () => void
  planned: boolean
}) {
  const navigate = useNavigate()
  const loose = group.supplierId === null
  const tickedCount = group.rows.filter(ticked).length
  const total = group.rows.reduce((sum, r) => sum + (ticked(r) ? costOf(r, amountOf(r)) : 0), 0)
  return (
    <section
      aria-label={group.name}
      // Not overflow-hidden: the From menus open over the card's edge. The header and the
      // last row carry the rounded corners instead.
      className="rounded-card border border-border bg-surface shadow-card [&>div:last-child]:rounded-b-card"
    >
      <div
        className={cx(
          'flex items-center gap-2.5 rounded-t-card px-3 py-2.5 sm:px-4',
          loose ? 'bg-soft' : 'bg-buy-wash',
        )}
      >
        {!loose && canEdit && (
          <input
            type="checkbox"
            aria-label={`All of ${group.name}`}
            className={cx(BOX, 'max-sm:hidden')}
            checked={tickedCount === group.rows.length}
            ref={(el) => {
              if (el) el.indeterminate = tickedCount > 0 && tickedCount < group.rows.length
            }}
            onChange={(e) => group.rows.forEach((r) => onTick(r.product.id, e.target.checked))}
          />
        )}
        <IconTile area={loose ? 'neutral' : 'buy'} size="sm">
          <Truck className="size-4" />
        </IconTile>
        <span className="min-w-0 flex-1">
          {loose ? (
            <b className="block font-bold">No usual supplier</b>
          ) : (
            <Link
              to={`/suppliers/${group.supplierId}`}
              className="block truncate font-bold text-ink hover:underline"
            >
              {group.name}
            </Link>
          )}
          <span className="block text-[12.5px] text-muted">
            {loose
              ? 'Choose one to put it on an order; it is remembered on the product.'
              : `${group.rows.length} product${group.rows.length === 1 ? '' : 's'}${total > 0 ? ` · ${money(total, settings)}` : ''}`}
          </span>
        </span>
        {!loose && total > 0 && (
          <b className="tabular text-[15px] max-sm:hidden">{money(total, settings)}</b>
        )}
        {!loose && canEdit && (
          <Button
            size="sm"
            variant="soft"
            className="h-[34px] text-[13px] max-sm:hidden"
            disabled={!planned}
            onClick={onMake}
          >
            <ClipboardList className="size-4" aria-hidden />
            Make draft order
          </Button>
        )}
        {canEdit && (
          <Menu
            label={`More for ${group.name}`}
            width={260}
            trigger={<Ellipsis className="size-4" aria-hidden />}
            triggerClassName="tap-target inline-flex size-[34px] items-center justify-center rounded-xl border border-border bg-surface text-muted transition-colors hover:bg-soft hover:text-ink"
          >
            <MenuItem icon={<Truck className="size-4" />} onSelect={onMoveAll}>
              <b className="block font-semibold">Move all to…</b>
              <span className="block text-xs text-muted">
                {group.rows.length === 1 ? 'This product' : `All ${group.rows.length} products`}, to
                another supplier
              </span>
            </MenuItem>
            {!loose && (
              <MenuItem
                icon={<Search className="size-4" />}
                onSelect={() => navigate(`/suppliers/${group.supplierId}`)}
              >
                Open {group.name}
              </MenuItem>
            )}
          </Menu>
        )}
      </div>
      {columns && (
        <div className={cx(GRID, 'px-4 py-2 text-table-head text-muted max-sm:hidden')}>
          <span />
          <span>Product</span>
          <span>In stock</span>
          <span className="text-right">On order</span>
          <span className="text-center">From</span>
          <span className="text-right">Order</span>
          <span className="text-right">Cost</span>
          <span className="text-right">Amount</span>
        </div>
      )}
      {group.rows.map((row) => {
        const p = row.product
        const unit = unitOf(row)
        const sym = unit?.symbol ?? ''
        const amount = amountOf(row)
        const on = ticked(row)
        return (
          <div
            key={p.id}
            aria-current={active === p.id ? 'true' : undefined}
            className={cx(
              GRID,
              'flex flex-wrap items-center gap-x-2.5 gap-y-2 border-t border-soft px-3 py-2.5 sm:px-4 sm:py-2',
              active === p.id && 'bg-buy-wash shadow-[inset_3px_0_0_var(--color-buy)]',
            )}
          >
            <input
              type="checkbox"
              aria-label={`Order ${p.name}`}
              className={BOX}
              disabled={loose || !canEdit}
              checked={on}
              onChange={(e) => onTick(p.id, e.target.checked)}
            />
            <span className="min-w-0 flex-1">
              <Link
                to={`/products/${p.id}`}
                className="block truncate font-semibold text-ink hover:underline"
              >
                {p.name}
              </Link>
              <span className="flex items-center gap-1.5 text-xs text-muted">
                <span className="sm:hidden">
                  <StockBadge row={row} sym={sym} />
                </span>
                {p.min_stock_milli > 0 && `alert at ${qty(p.min_stock_milli)} ${sym}`.trim()}
                {row.onOrderMilli > 0 && (
                  <span className="sm:hidden">· {qty(row.onOrderMilli)} on order</span>
                )}
              </span>
              {moved[p.id] && (
                <span className="block text-xs text-muted">
                  <span className="font-semibold text-primary">
                    {p.supplier_id ? `Usually ${nameOf(p.supplier_id)}` : 'No usual supplier'}
                  </span>{' '}
                  · this order only ·{' '}
                  <button
                    type="button"
                    onClick={() => onBack(row)}
                    className="font-semibold text-primary hover:underline"
                  >
                    Undo
                  </button>
                </span>
              )}
            </span>
            <span className="max-sm:hidden">
              <StockBadge row={row} sym={sym} />
            </span>
            <span
              className={cx(
                'tabular text-right max-sm:hidden',
                row.onOrderMilli > 0 ? 'font-semibold text-ink' : 'text-subtle',
              )}
            >
              {row.onOrderMilli > 0 ? `${qty(row.onOrderMilli)} ${sym}`.trim() : '–'}
            </span>
            {loose ? (
              <span className="max-sm:w-full sm:col-span-2 sm:justify-self-end sm:w-[220px]">
                {canEdit && (
                  <SupplierMenu
                    row={row}
                    current={null}
                    suppliers={suppliers}
                    onPick={(id, usual) => onMove([row], id, usual)}
                  />
                )}
              </span>
            ) : (
              <span className="justify-self-center max-sm:order-last">
                {canEdit && (
                  <SupplierMenu
                    row={row}
                    current={group.supplierId}
                    moved={!!moved[p.id]}
                    suppliers={suppliers}
                    onPick={(id, usual) => onMove([row], id, usual)}
                  />
                )}
              </span>
            )}
            {!loose && (
              <span className="flex items-center justify-self-end overflow-hidden rounded-xl border border-border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30">
                <button
                  type="button"
                  aria-label={`Less ${p.name}`}
                  disabled={!canEdit}
                  onClick={() => onStep(row, -1)}
                  className="flex size-9 shrink-0 items-center justify-center text-muted hover:bg-soft hover:text-ink pointer-coarse:size-11"
                >
                  <Minus className="size-4" />
                </button>
                <QtyInput
                  aria-label={`Order of ${p.name}`}
                  milli={amount}
                  allowsDecimal={unit?.allows_decimal ?? false}
                  disabled={!canEdit}
                  onValue={(m) => onAmount(p.id, m)}
                  className="w-14! min-w-0 rounded-none border-0 px-1! text-center font-semibold tabular focus:ring-0"
                />
                <button
                  type="button"
                  aria-label={`More ${p.name}`}
                  disabled={!canEdit}
                  onClick={() => onStep(row, 1)}
                  className="flex size-9 shrink-0 items-center justify-center text-muted hover:bg-soft hover:text-ink pointer-coarse:size-11"
                >
                  <Plus className="size-4" />
                </button>
              </span>
            )}
            <span className="tabular text-right text-muted max-sm:hidden">
              {p.cost_price_paisa === null ? '–' : money(p.cost_price_paisa, settings)}
            </span>
            {!loose && (
              <span
                className={cx('tabular text-right max-sm:hidden', on ? 'font-bold' : 'text-subtle')}
              >
                {p.cost_price_paisa === null ? '–' : money(costOf(row, amount), settings)}
              </span>
            )}
            {loose && <span className="max-sm:hidden" />}
          </div>
        )
      })}
    </section>
  )
}

/**
 * Where a product is ordered from: its row's From button (or "Choose a supplier" when it has
 * none). Picking a supplier moves the row at once; "Make it the usual supplier" saves it on
 * the product, and is ticked to start with for a product that has none.
 */
function SupplierMenu({
  row,
  current,
  moved = false,
  suppliers,
  onPick,
}: {
  row: ToOrderRow
  /** The supplier it is under on this page; null under "No usual supplier". */
  current: string | null
  /** Under another supplier for this order only. */
  moved?: boolean
  suppliers: SupplierRow[]
  onPick: (supplierId: string, usual: boolean) => void
}) {
  const p = row.product
  const loose = current === null
  return (
    <Menu
      label={`Supplier for ${p.name}`}
      // Low on the screen (a phone's bottom bar is there) it opens upwards.
      side="auto"
      title={loose ? undefined : `From ${suppliers.find((s) => s.id === current)?.name ?? ''}`}
      width={320}
      className={loose ? 'w-full' : undefined}
      trigger={
        loose ? (
          <>
            <span className="min-w-0 flex-1 truncate text-left font-semibold text-primary">
              Choose a supplier
            </span>
            <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden />
          </>
        ) : (
          <>
            <Truck className="size-4" aria-hidden />
            <ChevronDown className="size-3" aria-hidden />
          </>
        )
      }
      triggerClassName={cx(
        'inline-flex items-center rounded-lg text-sm transition-colors',
        loose
          ? 'h-9 w-full gap-2 border border-border bg-surface px-2.5 hover:border-primary/40'
          : 'tap-target h-[30px] gap-0.5 px-2',
        !loose &&
          (moved
            ? 'border border-dashed border-primary bg-sell-wash text-primary'
            : 'border border-border bg-surface text-muted hover:bg-soft hover:text-ink'),
      )}
    >
      <SupplierChoices row={row} current={current} suppliers={suppliers} onPick={onPick} />
    </Menu>
  )
}

/** The inside of the From menu: a search when the list is long, the suppliers, the tick box. */
function SupplierChoices({
  row,
  current,
  suppliers,
  onPick,
}: {
  row: ToOrderRow
  current: string | null
  suppliers: SupplierRow[]
  onPick: (supplierId: string, usual: boolean) => void
}) {
  const p = row.product
  const [query, setQuery] = useState('')
  // A product with no usual supplier keeps the one chosen, unless unticked.
  const [usual, setUsual] = useState(!p.supplier_id)
  const term = query.trim().toLowerCase()
  const active = suppliers.filter((s) => s.is_active || s.id === current)
  const shown = active
    .filter((s) => !term || s.name.toLowerCase().includes(term))
    // Where it is now and its usual supplier first, then by name.
    .sort((a, b) => rank(a.id) - rank(b.id) || a.name.localeCompare(b.name))
    .slice(0, 30)
  function rank(id: string) {
    return id === current ? 0 : id === p.supplier_id ? 1 : 2
  }
  return (
    <>
      <MenuLabel>Order {p.name} from</MenuLabel>
      {active.length > 6 && (
        <label className="mx-1 mb-1 flex items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-sm text-muted focus-within:border-primary">
          <Search className="size-4 shrink-0" aria-hidden />
          <input
            type="search"
            aria-label="Find a supplier"
            placeholder="Find a supplier…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-ink outline-none"
          />
        </label>
      )}
      <div className="max-h-64 overflow-y-auto">
        {shown.map((s) => (
          <MenuItem key={s.id} checked={s.id === current} onSelect={() => onPick(s.id, usual)}>
            <b className="block font-semibold">{s.name}</b>
            {s.id === p.supplier_id && (
              <span className="block text-xs text-muted">Usual supplier</span>
            )}
          </MenuItem>
        ))}
        {shown.length === 0 && (
          <p className="px-2.5 py-2 text-sm text-muted">No supplier by that name</p>
        )}
      </div>
      <MenuSeparator />
      <label className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2 text-sm hover:bg-soft">
        <input
          type="checkbox"
          checked={usual}
          onChange={(e) => setUsual(e.target.checked)}
          className={cx(BOX, 'mt-0.5')}
        />
        <span>
          <b className="block font-semibold">Make it the usual supplier</b>
          <span className="text-xs text-muted">
            {p.supplier_id
              ? 'Otherwise only this order changes.'
              : 'It has none yet, so this one is kept.'}
          </span>
        </span>
      </label>
    </>
  )
}

/**
 * Move products: from one supplier (or none) to another, all of them or the ones left
 * ticked. The same choice as a row's: this order only, unless "usual" is ticked.
 */
function MoveDialog({
  from: startFrom,
  groups,
  suppliers,
  busy,
  onClose,
  onMove,
}: {
  from: string
  groups: ReorderGroup[]
  suppliers: SupplierRow[]
  busy: boolean
  onClose: () => void
  onMove: (rows: ToOrderRow[], to: string, usual: boolean) => void
}) {
  const keyOf = (g: ReorderGroup) => g.supplierId ?? NONE
  const [from, setFrom] = useState(startFrom || (groups[0] ? keyOf(groups[0]) : ''))
  const [to, setTo] = useState('')
  const [usual, setUsual] = useState(false)
  const group = groups.find((g) => keyOf(g) === from)
  const [off, setOff] = useState<ReadonlySet<string>>(new Set())
  const rows = group?.rows ?? []
  const chosen = rows.filter((r) => !off.has(r.product.id))
  const toName = suppliers.find((s) => s.id === to)?.name
  const fromName = group?.name ?? ''
  const label = toName
    ? `Move ${chosen.length} product${chosen.length === 1 ? '' : 's'} to ${toName}`
    : 'Move products'
  return (
    <Modal
      open
      onClose={onClose}
      title="Move products to another supplier"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!toName || chosen.length === 0}
            onClick={() => onMove(chosen, to, usual)}
          >
            {label}
          </Button>
        </>
      }
    >
      <p className="-mt-2 mb-4 text-sm text-muted">For the draft orders made from this page.</p>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="grid min-w-40 flex-1 gap-1.5 text-sm font-semibold">
          From
          <Select
            value={from}
            onChange={(e) => {
              setFrom(e.target.value)
              setOff(new Set())
              if (e.target.value === to) setTo('')
            }}
          >
            {groups.map((g) => (
              <option key={keyOf(g)} value={keyOf(g)}>
                {g.name}
              </option>
            ))}
          </Select>
        </label>
        <label className="grid min-w-40 flex-1 gap-1.5 text-sm font-semibold">
          To
          <Select value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">Choose a supplier</option>
            {suppliers
              .filter((s) => s.is_active && s.id !== from)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </Select>
        </label>
      </div>
      <div className="overflow-hidden rounded-xl border border-border">
        <label className="flex items-center gap-2.5 px-3 py-2 text-table-head text-muted">
          <input
            type="checkbox"
            aria-label={`All of ${fromName}`}
            className={BOX}
            checked={chosen.length === rows.length && rows.length > 0}
            ref={(el) => {
              if (el) el.indeterminate = chosen.length > 0 && chosen.length < rows.length
            }}
            onChange={(e) =>
              setOff(e.target.checked ? new Set() : new Set(rows.map((r) => r.product.id)))
            }
          />
          <span className="flex-1">{fromName}’s products on this page</span>
          <span>
            {chosen.length} of {rows.length}
          </span>
        </label>
        <div className="max-h-64 overflow-y-auto">
          {rows.map((r) => {
            const on = !off.has(r.product.id)
            return (
              <label
                key={r.product.id}
                className="flex items-center gap-2.5 border-t border-soft px-3 py-2.5 text-sm"
              >
                <input
                  type="checkbox"
                  aria-label={`Move ${r.product.name}`}
                  className={BOX}
                  checked={on}
                  onChange={(e) =>
                    setOff((s) => {
                      const next = new Set(s)
                      if (e.target.checked) next.delete(r.product.id)
                      else next.add(r.product.id)
                      return next
                    })
                  }
                />
                <b className="min-w-0 flex-1 truncate font-semibold">{r.product.name}</b>
                <span className={cx('text-xs', on ? 'font-semibold text-primary' : 'text-subtle')}>
                  {on ? 'Moves' : `Stays with ${fromName}`}
                </span>
              </label>
            )
          })}
        </div>
      </div>
      <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-xl bg-soft px-3 py-2.5 text-sm">
        <input
          type="checkbox"
          checked={usual}
          onChange={(e) => setUsual(e.target.checked)}
          className={cx(BOX, 'mt-0.5')}
        />
        <span>
          <b className="block font-semibold">Make it their usual supplier</b>
          <span className="text-xs text-muted">
            Otherwise only this order changes; next time they show under their usual supplier again.
          </span>
        </span>
      </label>
    </Modal>
  )
}

function StockBadge({ row, sym }: { row: ToOrderRow; sym: string }) {
  return row.stockMilli <= 0 ? (
    <Badge tone="danger">
      {row.stockMilli < 0 ? `${qty(row.stockMilli)} ${sym}`.trim() : 'Out'}
    </Badge>
  ) : (
    <Badge tone="warning">{`${qty(row.stockMilli)} ${sym}`.trim()}</Badge>
  )
}
